import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import type { RootState } from '../index'

interface AudioRecorderState {
  isInitializingSession: boolean
  isRecording: boolean
  isSummarizing: boolean
  recordingSoapId: string | null
}

const initialState: AudioRecorderState = {
  isRecording: false,
  isSummarizing: false,
  recordingSoapId: null,
  isInitializingSession: false,
}

const audioRecorderSlice = createSlice({
  name: 'audioRecorder',
  initialState,
  reducers: {
    setRecording: (state, action: PayloadAction<boolean>) => {
      state.isRecording = action.payload
    },
    setSummarizing: (state, action: PayloadAction<boolean>) => {
      state.isSummarizing = action.payload
    },
    setRecordingSoapId: (state, action: PayloadAction<string | null>) => {
      state.recordingSoapId = action.payload
    },
    setInitializingSession: (state, action: PayloadAction<boolean>) => {
      state.isInitializingSession = action.payload
    },
  },
})

export const {
  setRecording,
  setSummarizing,
  setRecordingSoapId,
  setInitializingSession,
} = audioRecorderSlice.actions

export default audioRecorderSlice.reducer

// Selectors
export const getAudioRecorderState = (state: RootState): AudioRecorderState =>
  state.audioRecorder
export const getIsRecording = (state: RootState) =>
  getAudioRecorderState(state).isRecording
export const getIsSummarizing = (state: RootState) =>
  getAudioRecorderState(state).isSummarizing
export const getRecordingSoapId = (state: RootState) =>
  getAudioRecorderState(state).recordingSoapId
export const getIsInitializingSession = (state: RootState) =>
  getAudioRecorderState(state).isInitializingSession

/**
 * Convenience selector to check if any audio recorder operation is in progress.
 * UI components can use this to show loading states when any operation is active.
 */
export const getIsAudioRecorderBusy = (state: RootState) => {
  const recorderState = getAudioRecorderState(state)
  return (
    recorderState.isInitializingSession ||
    recorderState.isRecording ||
    recorderState.isSummarizing
  )
}
